import React, { useEffect, useState } from 'react';
import { useGridApiContext, GridFooterContainer, GridPagination } from '@mui/x-data-grid';
import { Box, Checkbox, FormControlLabel, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import * as XLSX from 'xlsx-js-style';
import { useBizTypeTranslation } from 'hooks/useBizTypeTranslation';
import { FileDownloadOutlined } from '@mui/icons-material';
import CustomDialog from 'components/CustomDialog';

export default function CustomFooter({ fileBaseName, displayValue, columnsList }) {
    const theme = useTheme();
    const { bt } = useBizTypeTranslation();
    const apiRef = useGridApiContext();
    const [openExportDialog, setOpenExportDialog] = useState(false);
    const [colsData, setColsData] = useState(null);
    const [selectedheaders, setSelectedHeaders] = useState([]);

    useEffect(() => {
        if (openExportDialog === false) return;

        const cols = columnsList.filter((c) => !c?.hideInExport &&
            !c.field?.startsWith('__') &&
            !c.type?.includes('actions'))

        if (!cols) return { headers: [], body: [], rowsAsObjects: [] };

        const rowsArr = Array.from(apiRef.current.getRowModels().values());
        const headers = cols?.map((c) => c.headerName || c.field);
        const fields = cols?.map((c) => c.field);
        const body = rowsArr?.map((r) => fields.map((f) => r[f]));

        const rowsAsObjects = rowsArr?.map((r) => {
            const obj = {};
            cols?.forEach((c) => { obj[c.headerName || c.field] = displayValue(r[c.field]); });
            return obj;
        });

        setSelectedHeaders(headers)
        setColsData({ headers, body, rowsAsObjects });
    }, [columnsList, openExportDialog]);

    const exportExcel = () => {
        const { rowsAsObjects, headers } = colsData;
        const newHeaders = headers.filter((h) => selectedheaders.includes(h));

        const rowsAsObjectsFiltered = rowsAsObjects.map(row =>
            newHeaders.reduce((acc, h) => {
                const value = row[h];
                acc[h] = value === undefined || value === null || value === "undefined" ? "" : value;
                return acc;
            }, {})
        );

        let ws = XLSX.utils.json_to_sheet(rowsAsObjectsFiltered);
        ws["!cols"] = newHeaders.map((h) => ({
            wch: Math.max(h.length, ...rowsAsObjects.map((r) => String(r[h] || "").length)) + 2
        }));

        newHeaders.forEach((header, index) => {
            const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
            ws[cellRef].s = {
                font: { bold: true, color: theme?.palette?.primary?.main ? { rgb: theme.palette.primary.main.replace('#', '') } : "" },
                alignment: { horizontal: "right" }
            };
        });

        rowsAsObjectsFiltered.forEach((row, rowIndex) => {
            newHeaders.forEach((header, colIndex) => {
                const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
                if (!ws[cellRef]) return; // במקרה של ערך ריק
                ws[cellRef].s = {
                    alignment: { horizontal: "right" }
                };
            });
        });
        const wb = XLSX.utils.book_new();
        wb.Workbook = { Views: [{ RTL: true }] };
        ws["!rtl"] = true;
        XLSX.utils.book_append_sheet(wb, ws, 'Data');
        XLSX.writeFile(wb, `${fileBaseName}.xlsx`);

        setOpenExportDialog(false)
    };

    const addRemoveColumn = (header) => {
        let updatedSelected = [...selectedheaders];
        if (updatedSelected.includes(header)) {
            updatedSelected = updatedSelected.filter((h) => h !== header);
        } else {
            updatedSelected.push(header);
        }
        setSelectedHeaders(updatedSelected);
    };

    return (
        <>
            <GridFooterContainer sx={{ px: 1, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}>
                <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">

                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip title={bt("EXPORT_TABLE")}>
                            <IconButton onClick={() => setOpenExportDialog(true)}><FileDownloadOutlined /></IconButton>
                        </Tooltip>
                    </Stack>

                    <Box><GridPagination /></Box>
                </Stack>
            </GridFooterContainer>

            <CustomDialog
                open={openExportDialog}
                onClose={() => setOpenExportDialog(false)}
                title="EXPORT_TABLE"
                closeBtn="CANCEL"
                minWidth={500}
                actionBtn={{ title: "EXPORT_EXCEL", onClick: () => exportExcel() }}
            >
                <Typography>
                    {bt("SHOW_FIELD")}:
                </Typography>
                <Grid container >
                    {colsData?.headers?.map((h) => {
                        const isSelected = selectedheaders.includes(h);
                        return (
                            <Grid item xs={12} md={4} key={h}>
                                <FormControlLabel
                                    value={isSelected}
                                    onChange={() => addRemoveColumn(h)}
                                    checked={isSelected}
                                    control={<Checkbox />}
                                    label={h}
                                />
                            </Grid>
                        )
                    }
                    )}
                </Grid>
            </CustomDialog>
        </>
    );
}
